import React, { useState } from "react";

import { DebtRecoveryContainer, SectionContainer } from "./debt-recovery-style";
import { images } from "../../../assets";
import GoBack from "../../../components/go-back";
import Filter from "./filter/filter";
import Tabs from "../../../components/tabs/tabs";
import { Button } from "../../../components/buttons";
import Table from "../../../components/table-simple/table";
import LeftInfoModal from "../../../components/left-info-modal";
import NoData from "./no-data";
import { LeftInfoModalProps } from "../../../components/left-info-modal/left-info-modal-layout";

import DebtRecoveryServices from "../../../services/DebtRecoveryServices";
import { formatTabsData, MODAL_SEND_DEBT_EMAIL_KO, MODAL_SEND_DEBT_EMAIL_OK } from "./helpers";
import { isValidEmail } from "../../../utils/validation";
import { deepGet } from "../../../utils/utility";
import {
  UserEmail,
  FilterFields,
  EmailModal,
  DebtRecoveryProps,
  TabsData,
} from "./debt-recovery-types";
import { GeneratePaymentRequest } from "../../../services/DebtRecoveryServices.dto";

const DebtRecovery = ({ mediator, setLoading }: DebtRecoveryProps) => {
  const emptyUserEmail: UserEmail = {
    value: "",
    errorCode: "",
  };
  const [filter, setFilter] = useState<FilterFields>({
    takerNif: "",
    mediatorCode: mediator.mediatorCode,
  });
  const [dataError, setDataError] = useState<string>("");
  const [tabs, setTabs] = useState<string[]>([]);
  const [tabsData, setTabsData] = useState<TabsData | null>(null);
  const [currentTab, setCurrentTab] = useState<string>("");
  const [emailModal, setEmailModal] = useState<EmailModal>({
    showModal: false,
    debtItem: "",
  });
  const [requestData, setRequestData] = useState<GeneratePaymentRequest>({
    takerNif: "",
    mediatorCode: "",
    birthDate: "",
    email: "",
  });
  const [userEmail, setUserEmail] = useState<UserEmail>(emptyUserEmail);
  const [responseModal, setResponseModal] = useState<Partial<LeftInfoModalProps> | null>(null);

  const handleOnGetData = async () => {
    cleanData();
    const response = await DebtRecoveryServices.getDebtReceipts({
      setLoading,
      loader: true,
      mediatorCode: filter.mediatorCode,
      takerNif: filter.takerNif,
    });

    if (response.ok) {
      const getFormatedTabsData = formatTabsData(response.data);
      setRequestData({
        takerNif: filter.takerNif,
        mediatorCode: filter.mediatorCode,
        birthDate: response.data.FechaNacimientoTomador,
        email: "",
      });
      setTabs(getFormatedTabsData.tabs);
      setTabsData(getFormatedTabsData.tabsData);
      setCurrentTab(getFormatedTabsData.tabs[0]);
      if (Object.keys(getFormatedTabsData.tabsData).length === 0) {
        setDataError("No se han encontrado recibos impagados.");
      }
    } else {
      setDataError(response.message);
    }
  };

  const handleOnClickRecoverDebt = (products?: string) => {
    setEmailModal({ showModal: true, debtItem: products || "todos los ramos" });
    if (products) {
      const codRamo = deepGet(tabsData, `${currentTab}.data.0.COD_RAMO`, "");
      const codSubramo = deepGet(tabsData, `${currentTab}.data.0.COD_SUBRAMO`, "");
      setRequestData((value) => ({ ...value, codRamo, codSubramo }));
    }
  };

  const handleSendEmail = async () => {
    if (!isValidEmail(userEmail.value)) {
      setUserEmail({ ...userEmail, errorCode: "invalid-email" });
      return;
    }
    setEmailModal({ showModal: false, debtItem: "" });

    const request = {
      ...requestData,
      email: userEmail.value,
    };
    const response = await DebtRecoveryServices.generatePaymentRequest({
      loader: true,
      setLoading,
      request,
    });
    if (response.ok) {
      setResponseModal(MODAL_SEND_DEBT_EMAIL_OK);
    } else {
      const modalContent = {
        ...MODAL_SEND_DEBT_EMAIL_KO,
        mainText: response.message || MODAL_SEND_DEBT_EMAIL_KO.mainText,
      };
      setResponseModal(modalContent);
    }
    setUserEmail(emptyUserEmail);
  };

  const cleanData = () => {
    setDataError("");
    setTabs([]);
    setTabsData(null);
    setCurrentTab("");
  };

  const getTabContent = () => {
    return (
      <div>
        {tabs && tabs.length > 0 && tabsData && tabsData[currentTab] && (
          <SectionContainer>
            <div className="button-container">
              <Button
                text={`Recobro recibos ${currentTab}`}
                onClick={() => handleOnClickRecoverDebt(currentTab)}
              />
            </div>
            <Table columns={tabsData[currentTab].columns} data={tabsData[currentTab].data} />
          </SectionContainer>
        )}
      </div>
    );
  };

  return (
    <DebtRecoveryContainer>
      <div className="black-arrow-container">
        <GoBack text="Volver" />
      </div>

      <div className="title-row">
        <div className="main-title-container">
          <h2>RECOBROS</h2>
        </div>
        {tabsData && (
          <div className="button-container">
            <Button text="Recobro recibos impagados" onClick={() => handleOnClickRecoverDebt()} />
          </div>
        )}
      </div>

      <Filter
        mediator={mediator}
        onGetData={handleOnGetData}
        filter={filter}
        setFilter={setFilter}
      />

      <SectionContainer>
        {dataError && <NoData message={dataError} />}

        {tabs.length > 0 && (
          <Tabs
            tabs={tabs}
            currentTab={currentTab}
            onClickTab={(tab: string) => setCurrentTab(tab)}
          >
            {currentTab !== undefined ? getTabContent() : null}
          </Tabs>
        )}
      </SectionContainer>

      <LeftInfoModal
        active={emailModal.showModal}
        img={images.IconContactCollapsedWhite}
        mainTitle="Envío de petición de recobro"
        mainText={`Por favor, ingrese un email para el envío de la petición de recobro de los productos de ${emailModal.debtItem}`}
        buttonText="Enviar"
        onClickAccept={handleSendEmail}
        close={() => setEmailModal({ ...emailModal, showModal: false })}
        withInput={{
          labelText: "email",
          value: userEmail.value,
          errorCode: userEmail.errorCode,
          onChange: (value: string) => setUserEmail({ value, errorCode: "" }),
        }}
      />

      {responseModal && (
        <LeftInfoModal
          active={responseModal.active}
          img={responseModal.img}
          mainTitle={responseModal.mainTitle}
          mainText={responseModal.mainText}
          close={() => setResponseModal(null)}
          closeButtonText="Aceptar"
          noMainButton
          centerPrimaryButton
        />
      )}
    </DebtRecoveryContainer>
  );
};

export default DebtRecovery;
